import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ApprovalItem } from '../client';
import { useApi } from '../state/ApiContext';
import { useApprovalEvents } from '../state/ApprovalEventsContext';
import { ApprovalCardHeader } from './ApprovalCardHeader';
import AsyncButton from '../components/AsyncButton';
import { GuardedActionButton } from '../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../permissions/permission-catalog';
import './approvals.css';

type DecisionPending = 'approved' | 'rejected' | null;

/**
 * The built-in roles an approver may confer, least privilege first. The
 * request pre-fills one (viewer when the agent named none); the backend bounds
 * whatever is chosen by the approver's own authority, so Administrator is
 * offered here and refused server-side for anyone who is not one.
 */
const INVITE_ROLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'manager', label: 'Manager' },
  { value: 'billing', label: 'Billing' },
  { value: 'administrator', label: 'Administrator' },
];
const LEAST_PRIVILEGE_INVITE_ROLE = 'viewer';

function roleLabel(value: string): string {
  return (
    INVITE_ROLE_OPTIONS.find((option) => option.value === value)?.label || value
  );
}

function requestedRoleOf(detail: Record<string, unknown> | undefined): string {
  const value = String(detail?.builtinRole || '');
  return INVITE_ROLE_OPTIONS.some((option) => option.value === value)
    ? value
    : LEAST_PRIVILEGE_INVITE_ROLE;
}

/**
 * The one template for invite_user approvals, used on the team home page,
 * in chats and in card threads. Self-contained: loads by approvalId,
 * re-loads on approval events, and shows the final outcome once decided —
 * wherever the decision happened.
 *
 * `headerless` omits the shared agent/origin header — used when a wrapper
 * (e.g. the team-home `ApprovalRequestView`) already renders it, to avoid a
 * doubled header.
 */
export const InviteApprovalCard: React.FC<{
  approvalId: number;
  headerless?: boolean;
}> = ({ approvalId, headerless = false }) => {
  const { approvalsApi } = useApi();
  const { epoch } = useApprovalEvents();
  const [approval, setApproval] = useState<ApprovalItem | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(LEAST_PRIVILEGE_INVITE_ROLE);
  const [note, setNote] = useState('');
  const [decisionPending, setDecisionPending] = useState<DecisionPending>(null);
  const [error, setError] = useState<string | null>(null);
  const edited = useRef(false);
  const roleEdited = useRef(false);

  const load = useCallback(async () => {
    const response = await approvalsApi.apiApprovalsApprovalIdGet(approvalId);
    const next = response.data.approval as ApprovalItem;
    setApproval(next);
    if (!edited.current) {
      setEmail(String(next.detail?.email || ''));
    }
    if (!roleEdited.current) {
      setRole(requestedRoleOf(next.detail as Record<string, unknown>));
    }
  }, [approvalId, approvalsApi]);

  useEffect(() => {
    load().catch(() => setError('Could not load this invite request.'));
  }, [load, epoch]);

  const decide = async (decision: 'approved' | 'rejected') => {
    setDecisionPending(decision);
    setError(null);
    try {
      await approvalsApi.apiApprovalsApprovalIdDecisionPost(approvalId, {
        decision,
        note: note.trim() || undefined,
        ...(decision === 'approved'
          ? { inputs: { email: email.trim().toLowerCase(), builtinRole: role } }
          : {}),
      });
    } catch {
      setError('Could not save the decision. It may already be resolved.');
    } finally {
      await load();
      setDecisionPending(null);
    }
  };

  const testId = `invite-approval-${approvalId}`;

  if (!approval) {
    // When headerless, the wrapper already renders the card box — don't add
    // a second one just for the loading/error placeholder.
    return headerless ? (
      <div data-testid={testId}>{error || 'Loading invite request…'}</div>
    ) : (
      <div className="approval-card approval-card-invite" data-testid={testId}>
        {error || 'Loading invite request…'}
      </div>
    );
  }

  const pending = approval.status === 'pending';
  const deciding = decisionPending !== null;
  const invitedEmail = String(
    approval.detail?.invitedEmail || approval.detail?.email || '',
  );
  // Supplied by the agent when the team member gave a name with the address.
  // Display only — nothing persists it past the decision.
  const personName = String(approval.detail?.name || '').trim();
  // What the person will actually join with: the role the approver conferred,
  // or the requested one for a request decided before roles were recorded.
  const grantedRole = String(
    approval.detail?.invitedRole ||
      requestedRoleOf(approval.detail as Record<string, unknown>),
  );

  const body = (
    <>
      {!headerless && (
        <ApprovalCardHeader
          teamId={approval.teamId}
          agent={approval.requestingAgent}
          requestingUser={approval.requestingUser}
          requestingTrigger={approval.requestingTrigger}
          origin={approval.origin}
          createdAt={approval.createdAt}
          title={approval.title}
        />
      )}
      {approval.reason && (
        <div className="approval-reason">
          <span>The agent's stated reason</span>
          <p>{approval.reason}</p>
        </div>
      )}
      {error && <div className="projects-error">{error}</div>}
      {pending ? (
        <>
          {personName && (
            <div className="approval-invite-person">
              <span>Who</span>
              <p data-testid={`${testId}-person`}>{personName}</p>
            </div>
          )}
          <label className="approval-note">
            <span>Email address to invite</span>
            <input
              type="email"
              value={email}
              disabled={deciding}
              onChange={(event) => {
                edited.current = true;
                setEmail(event.target.value);
              }}
            />
          </label>
          <label className="approval-note">
            <span>
              Role on the team{' '}
              <small>
                (the agent asked for{' '}
                {roleLabel(
                  requestedRoleOf(approval.detail as Record<string, unknown>),
                )}
                )
              </small>
            </span>
            <select
              aria-label="Role on the team"
              value={role}
              disabled={deciding}
              onChange={(event) => {
                roleEdited.current = true;
                setRole(event.target.value);
              }}>
              {INVITE_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="approval-note">
            <span>
              Note to agent <small>(optional)</small>
            </span>
            <textarea
              aria-label="Note to agent"
              maxLength={4000}
              disabled={deciding}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <div className="approval-actions">
            {/* Both decisions are one route, POST
                /api/approvals/:approvalId/decision, `approval: manage` —
                declining is as much an exercise of the decision as approving. */}
            <GuardedActionButton
              as={AsyncButton}
              resource={PermissionResource.APPROVAL}
              level={PermissionLevel.MANAGE}
              verb="decide"
              subject="pending approvals"
              testId="invite-approval-reject-denied"
              className="approval-reject"
              disabled={deciding}
              loading={decisionPending === 'rejected'}
              loadingText="Rejecting..."
              onAllowed={() => void decide('rejected')}>
              Reject
            </GuardedActionButton>
            <GuardedActionButton
              as={AsyncButton}
              resource={PermissionResource.APPROVAL}
              level={PermissionLevel.MANAGE}
              verb="decide"
              subject="pending approvals"
              testId="invite-approval-approve-denied"
              className="approval-approve"
              disabled={deciding || !email.trim()}
              loading={decisionPending === 'approved'}
              loadingText="Approving..."
              onAllowed={() => void decide('approved')}>
              Approve &amp; send invite
            </GuardedActionButton>
          </div>
        </>
      ) : (
        <div
          className={`approval-invite-status approval-invite-status-${approval.status}`}
          role="status">
          {approval.status === 'approved'
            ? `Approved — invitation sent to ${
                personName ? `${personName} at ${invitedEmail}` : invitedEmail
              } as ${roleLabel(grantedRole)}.`
            : approval.status === 'rejected'
              ? `Rejected${approval.decisionNote ? ` — ${approval.decisionNote}` : '.'}`
              : `This request is ${approval.status}.`}
        </div>
      )}
    </>
  );

  // headerless: the wrapper (e.g. ApprovalRequestView on team home) owns the
  // outer card box and header — render body content only, no second box.
  if (headerless) {
    return <div data-testid={testId}>{body}</div>;
  }

  return (
    <article
      className="approval-card approval-card-invite"
      data-testid={testId}>
      {body}
    </article>
  );
};

export default InviteApprovalCard;
